/**
 * EmDispatchLanding — public marketing page for the AI Emergency Dispatch
 * Intelligence Platform. Sits at `/emdispatch` and CTAs into `/emdispatch/login`.
 *
 * Motion is deliberate — subtle animate-in on scroll, radar pulse behind the
 * hero mark, and a shimmering feature grid. Nothing overwhelming.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  SirenIcon, Hospital, Brain, ChatCircleText, MonitorPlay,
  Microphone, Truck, MapPin, ShieldCheck, ChartLine, Warning, Fire,
  ArrowRight, CheckCircle, Clock, Sparkle,
} from "@phosphor-icons/react";

export default function EmDispatchLanding() {
  useEffect(() => { document.title = "EmDispatch · AI Emergency Dispatch"; window.scrollTo(0, 0); }, []);
  const reduced = useReducedMotion();

  return (
    <div className="min-h-screen text-white overflow-x-hidden"
          style={{ background: "radial-gradient(ellipse at 20% 0%, #1E1B4B 0%, #0F172A 55%, #020617 100%)" }}
          data-testid="emd-landing">
      {/* Top bar */}
      <header className="relative z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#DC2626] grid place-items-center shadow-[0_6px_22px_rgba(220,38,38,0.55)]">
            <SirenIcon size={17} weight="fill"/>
          </div>
          <div className="font-heading text-lg tracking-tight">EmDispatch</div>
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/40 hidden sm:inline">Emergency Dispatch Intelligence</span>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/emdispatch/login"
                  data-testid="emd-land-login"
                  className="h-9 px-4 rounded-md bg-white text-[#0F172A] font-bold text-xs inline-flex items-center gap-1.5 hover:bg-white/90 transition-colors">
              Hospital sign-in <ArrowRight size={11} weight="bold"/>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-6 pb-28 md:pb-40">
        {/* Radar pulses */}
        {!reduced && (
          <div className="absolute inset-0 pointer-events-none flex items-start justify-center">
            <div className="relative w-[600px] h-[600px] mt-24 opacity-40">
              {[0, 1, 2].map(i => (
                <span key={i} className="absolute inset-0 rounded-full border border-[#DC2626]"
                       style={{ animation: `emd-radar 3.6s ${i * 1.2}s infinite ease-out` }}/>
              ))}
            </div>
          </div>
        )}
        <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.6 }}
                       className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>
            LIVE for hospital dispatch teams
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.65, delay: 0.05 }}
                       className="font-heading text-4xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight mt-6">
            From call <span className="text-[#DC2626]">→</span> to responder.<br/>
            <span className="text-white/70">In seconds, not minutes.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                     transition={{ delay: 0.4, duration: 0.6 }}
                     className="mt-6 text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            EmDispatch is an AI-triaged emergency dispatch console for hospitals.
            Every ambulance call, walk-in and phoned-in report is auto-classified
            RED / YELLOW / GREEN — dispatchers see the whole board in one screen
            and act in one tap.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ delay: 0.55, duration: 0.5 }}
                       className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/emdispatch/login"
                  data-testid="emd-land-cta"
                  className="h-12 px-6 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-sm inline-flex items-center gap-2 shadow-[0_10px_40px_rgba(220,38,38,0.45)]">
              Sign into console <ArrowRight size={13} weight="bold"/>
            </Link>
            <a href="#features"
                className="h-12 px-6 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm inline-flex items-center gap-2 border border-white/10">
              See what it does
            </a>
          </motion.div>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-5 md:gap-8 text-[11px] uppercase tracking-[0.24em] text-white/40">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="fill" className="text-emerald-400"/>HIPAA-aware</span>
            <span className="inline-flex items-center gap-1.5"><Brain size={12} weight="fill" className="text-purple-400"/>AI-triaged</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={12} weight="fill" className="text-[#DC2626]"/>Real-time</span>
            <span className="inline-flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-amber-400"/>Voice intake</span>
          </div>
        </div>

        <TriageOrbs/>
      </section>

      {/* Features */}
      <section id="features" className="relative py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <SectionEyebrow>The platform</SectionEyebrow>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight mt-3 max-w-3xl">
            Every second between a call and a responder — <span className="text-[#DC2626]">removed</span>.
          </h2>
          <p className="text-white/60 mt-4 max-w-2xl leading-relaxed">
            EmDispatch consolidates guided call-taking, ambulance booking,
            hospital triage and unit dispatch into one console. No more juggling
            radios, sheets and phone lines.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES.map((f, i) => <FeatureCard key={i} {...f} idx={i}/>)}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative py-20 md:py-28 bg-black/20 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <SectionEyebrow>Signal → action</SectionEyebrow>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight mt-3 max-w-3xl">
            Three steps from the ring to the responder.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
            <StepCard n="01" Icon={ChatCircleText} title="Capture"
                       desc="Guided intake or 30-sec voice note — Whisper transcribes; every call becomes structured data."/>
            <StepCard n="02" Icon={Brain} title="Triage"
                       desc="On submit, a purpose-tuned LLM classifies RED / YELLOW / GREEN and writes a dispatch recommendation."/>
            <StepCard n="03" Icon={Truck} title="Dispatch"
                       desc="Detail drawer shows AI recommendation, road distance + ETA, one-tap advance to enroute → arrived → closed."/>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatOrb value="< 8s" label="Call to triaged"/>
          <StatOrb value="RED / YEL / GRN" label="AI priorities"/>
          <StatOrb value="30 s" label="Voice-to-transcript"/>
          <StatOrb value="24 × 7" label="Auto refresh"/>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight">
            Ready to modernise your dispatch?
          </h2>
          <p className="text-white/60 mt-4 leading-relaxed">
            Get onboarded by the Rescigo team, receive dispatcher credentials,
            and go live in a day.
          </p>
          <Link to="/emdispatch/login"
                data-testid="emd-land-cta2"
                className="mt-8 inline-flex h-12 px-6 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-sm items-center gap-2 shadow-[0_10px_40px_rgba(220,38,38,0.45)]">
            Hospital sign-in <ArrowRight size={13} weight="bold"/>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 text-[11px] uppercase tracking-[0.24em] text-white/40 flex flex-wrap items-center justify-between gap-2">
          <span>© Rescigo Health · EmDispatch</span>
          <Link to="/" className="hover:text-white transition-colors">← Main site</Link>
        </div>
      </footer>

      <style>{`
        @keyframes emd-radar {
          0%   { transform: scale(0.55); opacity: 0.55 }
          100% { transform: scale(1.15); opacity: 0 }
        }
        @keyframes emd-orb-float {
          0%   { transform: translateY(0px) }
          50%  { transform: translateY(-8px) }
          100% { transform: translateY(0px) }
        }
      `}</style>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#DC2626]">
      <span className="w-4 h-px bg-[#DC2626]"/> {children}
    </div>
  );
}

const FEATURES = [
  { Icon: Brain,         accent: "#A855F7", title: "AI triage engine",
    desc: "Every incident is auto-classified RED/YELLOW/GREEN by a purpose-tuned LLM. A 40-keyword heuristic backs it up so the queue never stalls." },
  { Icon: ChatCircleText, accent: "#F59E0B", title: "Guided call-taking",
    desc: "Structured intake — patient, complaint, symptoms, address, landmark — with instant AI classification on submit." },
  { Icon: Microphone,    accent: "#F43F5E", title: "Voice intake · Whisper",
    desc: "Dispatchers record a 30-sec voice note. Whisper transcribes; the AI triage runs automatically on the transcript." },
  { Icon: MonitorPlay,   accent: "#22D3EE", title: "Single-screen board",
    desc: "Three columns — RED / YELLOW / GREEN — live 15-second refresh, priority + status filter chips, one-tap detail drawer." },
  { Icon: Truck,         accent: "#DC2626", title: "Ambulance auto-link",
    desc: "Every customer ambulance booking with a drop hospital lands as an inbound incident on that hospital's console — with vehicle class & ETA." },
  { Icon: MapPin,        accent: "#10B981", title: "Real road distance",
    desc: "OSRM-powered driving distance + travel time replaces straight-line math. Ops see the ETA the responder will actually clock." },
  { Icon: Warning,       accent: "#F43F5E", title: "Auto-escalation",
    desc: "108-tier emergencies force-promote to RED regardless of keyword scan — the classifier can't downgrade urgent calls by accident." },
  { Icon: ChartLine,     accent: "#22D3EE", title: "24-h dashboard",
    desc: "Priority counters, active workload and last-24-h volume update in real time. Every action logged for audit." },
  { Icon: ShieldCheck,   accent: "#A7F3D0", title: "Role-based staff",
    desc: "Hospital-admin creates dispatcher accounts with granular permissions (view / triage / dispatch / close / manage staff)." },
];

function FeatureCard({ Icon, accent, title, desc, idx }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                 viewport={{ once: true, margin: "-80px" }}
                 transition={{ duration: 0.5, delay: (idx % 3) * 0.06 }}
                 className="relative group rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:border-white/20 hover:bg-white/[0.06] transition-colors overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15 blur-3xl transition-opacity duration-500 group-hover:opacity-30"
           style={{ background: accent }}/>
      <div className="relative">
        <div className="w-11 h-11 rounded-lg grid place-items-center mb-4"
             style={{ background: `${accent}20`, color: accent }}>
          <Icon size={19} weight="duotone"/>
        </div>
        <div className="font-heading text-lg leading-tight">{title}</div>
        <div className="text-[13px] text-white/60 mt-2 leading-relaxed">{desc}</div>
      </div>
    </motion.div>
  );
}

function StepCard({ n, Icon, title, desc }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                 viewport={{ once: true, margin: "-60px" }}
                 transition={{ duration: 0.5 }}
                 className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 relative overflow-hidden">
      <div className="absolute top-4 right-4 text-white/10 font-mono text-3xl font-black">{n}</div>
      <div className="w-11 h-11 rounded-lg grid place-items-center bg-[#DC2626]/20 text-[#DC2626] mb-4">
        <Icon size={19} weight="duotone"/>
      </div>
      <div className="font-heading text-lg leading-tight">{title}</div>
      <div className="text-[13px] text-white/60 mt-2 leading-relaxed">{desc}</div>
    </motion.div>
  );
}

function StatOrb({ value, label }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                 viewport={{ once: true }}
                 className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 text-center">
      <div className="font-heading font-black text-2xl md:text-3xl text-white leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-2">{label}</div>
    </motion.div>
  );
}

function TriageOrbs() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => (x + 1) % 3), 2200);
    return () => clearInterval(t);
  }, []);
  const items = [
    { Icon: Fire,        label: "RED · Critical",   c: "#DC2626", d: "Chest pain · Cardiac arrest · Stroke" },
    { Icon: Warning,     label: "YELLOW · Urgent",  c: "#FBBF24", d: "Fracture · Fever · Fall injury" },
    { Icon: CheckCircle, label: "GREEN · Routine",  c: "#10B981", d: "Post-op transfer · Elder transport" },
  ];
  return (
    <div className="relative max-w-5xl mx-auto mt-16 px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-3">
      {items.map((it, i) => {
        const active = i === tick;
        const Icon = it.Icon;
        return (
          <div key={i}
               className={`relative rounded-2xl border p-4 backdrop-blur transition-all duration-500 ${
                 active ? "bg-white/[0.10] border-white/25 -translate-y-1 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
                        : "bg-white/[0.04] border-white/10"
               }`}
               style={{ animation: !active ? "emd-orb-float 4s ease-in-out infinite" : "none" }}>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg grid place-items-center"
                   style={{ background: it.c, color: "#fff" }}>
                <Icon size={16} weight="fill"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: it.c }}>{it.label}</div>
                <div className="text-[11px] text-white/60">{it.d}</div>
              </div>
              {active && (
                <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold"
                      style={{ background: it.c, color: "#fff" }}>
                  Live
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
